import styled, { css } from "styled-components";

// Main container for the entire jobs page
export const JobsContainer = styled.div`
  padding: 32px;
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

// Header section with title and button
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

// New container for title and job count
export const TitleContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

export const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

// New job count component
export const JobCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  background-color: #f3f4f6;
  padding: 4px 12px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
`;

// Modern card-style table container
export const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid #e5e7eb;
  max-width: 100%;
  margin: 0 auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  th {
    background-color: #f9fafb;
    color: #6b7280;
    font-weight: 500;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  td {
    padding: 16px;
    border-bottom: 1px solid #f3f4f6;
    color: #111827;
    font-size: 14px;
    vertical-align: middle;
    background-color: white;
  }

  tbody tr {
    transition: background-color 0.15s ease;
  }

  tbody tr:hover {
    background-color: #f9fafb;
  }

  tbody tr:hover td {
    background-color: #f9fafb;
  }

  tr:last-child td {
    border-bottom: none;
  }

  /* Clean row styling - no alternating colors */
  tr:nth-child(even),
  tr:nth-child(odd) {
    background-color: transparent;
  }
`;

// Improved status styling
export const StatusContainer = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;

  ${({ status }) => {
    if (status === "ACTIVE") {
      return css`
        background-color: #d1fae5;
        color: #065f46;
      `;
    }
    return css`
      background-color: #fee2e2;
      color: #991b1b;
    `;
  }}
`;

export const StatusIndicator = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 6px;
  background-color: ${(props) => (props.active ? "#10b981" : "#ef4444")};
`;

// Modern button styling
export const JobButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;

  ${({ isBooked, hasCampaignId }) => {
    if (isBooked) {
      return css`
        background-color: #10b981;
        color: white;
        &:hover {
          background-color: #059669;
        }
      `;
    }

    if (hasCampaignId) {
      return css`
        background-color: #3b82f6;
        color: white;
        &:hover {
          background-color: #2563eb;
        }
      `;
    }

    return css`
      background-color: #6366f1;
      color: white;
      &:hover {
        background-color: #5b21b6;
      }
    `;
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Improved channels styling
export const ChannelsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

export const ChannelItem = styled.span`
  background-color: #f3f4f6;
  color: #4b5563;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`;

// URL styling
export const JobUrl = styled.a`
  color: #3b82f6;
  text-decoration: none;
  font-weight: 400;
  max-width: 250px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
`;

// Location styling
export const LocationText = styled.span`
  color: #4b5563;
  font-size: 14px;
  font-weight: 400;
`;

// Company name styling
export const CompanyName = styled.span`
  font-weight: 500;
  color: #111827;
`;

// Job title styling
export const JobTitle = styled.span`
  font-weight: 600;
  color: #111827;
`;

// Styled components for previously inline styles
export const NotDefinedText = styled.span`
  color: #9ca3af;
  font-style: italic;
  font-size: 14px;
`;

export const NotSetText = styled.span`
  color: #9ca3af;
  font-style: italic;
  font-size: 14px;
`;

// Loading and error states
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
  font-size: 16px;
  color: #6b7280;
`;

export const Loader = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const ButtonLoader = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const SkeletonRow = styled.tr`
  td {
    height: 60px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
  }

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

export const NoPermissionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
  max-width: 600px;
`;

export const NoPermissionText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
  color: #6b7280;
  line-height: 1.5;
`;

// Empty state styling
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  text-align: center;
`;

export const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

export const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

export const EmptyStateText = styled.p`
  font-size: 14px;
  color: #6b7280;
  max-width: 400px;
  line-height: 1.5;
`;

// Add some additional styling for better visual separation
export const TableRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

export const TableCell = styled.td`
  padding: 16px;
  vertical-align: middle;

  &:first-child {
    padding-left: 20px;
  }

  &:last-child {
    padding-right: 20px;
  }
`;

// New date text component
export const DateText = styled.span`
  color: #4b5563;
  font-size: 14px;
  font-weight: 400;
  font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace;
`;

// Enhanced introduction components
export const IntroductionContainer = styled.div`
  max-width: 300px;
`;

export const IntroductionText = styled.div`
  color: #4b5563;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background-color: #f8fafc;
  padding: 10px 12px;
  border-radius: 6px;
  border-left: 3px solid #cbd5e1;
  position: relative;

  /* Subtle shadow for depth */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  /* Hover effect to indicate there's more content */
  cursor: ${(props) => (props.hasMore ? "help" : "default")};

  &:hover {
    background-color: ${(props) => (props.hasMore ? "#f1f5f9" : "#f8fafc")};
  }

  /* Add a small indicator when text is truncated */
  ${(props) =>
    props.hasMore &&
    css`
      &::after {
        content: "";
        position: absolute;
        bottom: 4px;
        right: 8px;
        width: 4px;
        height: 4px;
        background-color: #64748b;
        border-radius: 50%;
        opacity: 0.5;
      }
    `}
`;

export const IntroductionLabel = styled.span`
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
  display: block;
  margin-bottom: 4px;
`;
